<template>
  <div class="prompt-container">
    <!-- 导航栏 -->
    <HeaderComponent />

    <!-- 页面标题 -->
    <section class="page-header">
      <div class="container">
        <h1 class="page-title">提示词中心</h1>
        <p class="page-subtitle">AI各个平台/大模型常用的优质提示词集中地，一次性掌握全部好用提示词！</p>
      </div>
    </section>

    <!-- 主内容区域 -->
    <section class="main-content">
      <div class="container">
        <!-- 筛选和搜索区域 -->
        <div class="filter-search">
          <div class="search-box">
            <font-awesome-icon :icon="['fas', 'search']" class="search-icon" />
            <input type="text" placeholder="搜索提示词..." v-model="filter.search">
            <button class="search-btn" @click="handleSearch">搜索</button>
          </div>
          <div class="filter-buttons">
            <button 
              v-for="label in labels" 
              :key="label.id"
              class="filter-btn"
              :class="{ active: filter.label_id === label.id }"
              @click="handleLabelChange(label.id)"
            >
              {{ label.name }}
              <span class="count">({{ label.cnt }})</span>
            </button>
          </div>
        </div>

        <!-- 提示词列表 -->
        <div class="prompt-grid">
          <div 
            v-for="prompt in paginatedPrompts" 
            :key="prompt.id"
            class="prompt-card"
          >
            <div class="prompt-card-header">
              <div class="prompt-header-content">
                <h3 class="prompt-title">{{ prompt.title }}</h3>
                <div class="prompt-header-meta">
                  <div class="prompt-category"> {{ prompt.label_name }} </div>
                  <TagTypeComponent v-if="prompt.level" :tagType="prompt.level" />
                </div>
              </div>
            </div>
            <div class="prompt-card-body">
              <div class="prompt-content">
                <pre>{{ prompt.description }}</pre>
              </div>
              <div class="prompt-input">
                用户输入: {{ prompt.user_input }}
              </div>
              <div class="prompt-tags">
                <span v-for="tag in prompt.tags" :key="tag" class="tag">{{ tag }}</span>
              </div>
            </div>
            <div class="prompt-card-footer">
              <div class="prompt-meta">
                <span class="views"><font-awesome-icon :icon="['fas', 'eye']" /> {{ prompt.watch_count }}</span>
                <span class="stars"><font-awesome-icon :icon="['fas', 'star']" /> {{ prompt.like_count }}</span>
                <span class="stars"><font-awesome-icon :icon="['fas', 'copy']" /> {{ prompt.copy_count }}</span>
                <span class="time"><font-awesome-icon :icon="['fas', 'clock']" /> {{ prompt.create_at }}</span>
              </div>
              <div class="prompt-actions">
                <button class="btn btn-primary btn-small" @click="copyPrompt(prompt.user_input)">
                  <font-awesome-icon :icon="['fas', 'copy']" /> 复制
                </button>
                <button class="btn btn-secondary btn-small" @click="showDetail(prompt)">详情</button>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <PaginationComponent 
          v-model:currentPage="currentPage"
          :totalPages="totalPages"
        />
      </div>
    </section>

    <!-- 页脚 -->
    <FooterComponent />

    <!-- 提示词详情视图 -->
    <PromptDetail 
      v-if="selectedPrompt" 
      :prompt="selectedPrompt" 
      @close="hideDetail"
    />
  </div>
</template>
<script>
import HeaderComponent from '../components/Header.vue'
import FooterComponent from '../components/Footer.vue'
import PaginationComponent from '../components/PaginationComponent.vue'
import TagTypeComponent from '../components/TagTypeComponent.vue'
import PromptDetail from './PromptDetail.vue'
import { api } from '../api/index'

export default {
  name: 'PromptView',
  components: {
    HeaderComponent,
    FooterComponent,
    PaginationComponent,
    TagTypeComponent,
    PromptDetail
  },
  data() {
    return {
      filter: {
        label_id: null,
        search: ''
      },
      
      currentPage: 1,
      pageSize: 12,
      selectedPrompt: null,
      labels: [],
      prompts: [],
      totalPages: 1,
      loading: false
    }
  },
  computed: {
    paginatedPrompts() {
      return this.prompts
    }
  },
  async mounted() {
    await this.loadLabels()
    await this.loadPrompts()
  },
  methods: {
    async loadLabels() {
      try {
        const labels = await api.promptWord.getLabels()
        const totalCnt = labels.reduce((sum, label) => sum + (label.cnt || 0), 0)
        this.labels = [
          { id: null, name: '全部', cnt: totalCnt },
          ...labels
        ]
      } catch (error) {
        console.error('加载标签失败:', error)
      }
    },
    async loadPrompts() {
      if (this.loading) return
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize
        }
        if (this.filter.search) {
          params.search = this.filter.search
        }
        if (this.filter.label_id !== null) {
          params.label_id = this.filter.label_id
        }
        const result = await api.promptWord.getList(params)
        this.prompts = result.items || []
        this.totalPages = result.total_page
      } catch (error) {
        console.error('加载提示词失败:', error)
      } finally {
        this.loading = false
      }
    },
    handleLabelChange(label_id) {
      this.filter.label_id = label_id
      this.currentPage = 1
      this.loadPrompts()
    },
    handleSearch() {
      this.currentPage = 1
      this.loadPrompts()
    },
    copyPrompt(content) {
      navigator.clipboard.writeText(content).then(() => {
        alert('提示词已复制到剪贴板！')
      }).catch(err => {
        console.error('复制失败:', err)
      })
    },
    showDetail(prompt) {
      this.selectedPrompt = prompt
    },
    hideDetail() {
      this.selectedPrompt = null
    }
  },
  watch: {
    currentPage() {
      this.loadPrompts()
    }
  }
}
</script>

<style scoped>
.prompt-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}


/* 页面标题 */
.page-header {
  background-color: #f8f9fa;
  padding: 120px 0 40px;
  border-bottom: 1px solid #eee;
}

.page-title {
  font-size: 36px;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
}

.page-subtitle {
  font-size: 18px;
  color: #666;
}

/* 主内容区域 */
.main-content {
  padding: 40px 0;
  min-height: calc(100vh - 400px);
}

/* 筛选和搜索区域 */
.filter-search {
  margin-bottom: 40px;
}

.search-box {
  position: relative;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  z-index: 1;
}

.search-box input {
  flex: 1;
  padding: 12px 15px 12px 45px;
  border: 1px solid #ddd;
  border-radius: 5px 0 0 5px;
  font-size: 16px;
  transition: border-color 0.3s;
}

.search-btn {
  padding: 12px 24px;
  background-color: #667eea;
  color: white;
  border: none;
  border-radius: 0 5px 5px 0;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.search-btn:hover {
  background-color: #5568d3;
}

.search-box input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

.filter-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  background-color: white;
  border-radius: 20px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
  color: #666;
}

.filter-btn:hover {
  border-color: #667eea;
  color: #667eea;
}

.filter-btn.active {
  background-color: #667eea;
  border-color: #667eea;
  color: white;
}

.filter-btn .count {
  margin-left: 5px;
  font-size: 12px;
  opacity: 0.8;
}

/* 提示词列表 */
.prompt-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 25px;
  margin-bottom: 40px;
}

.prompt-card {
  background-color: #f0f5ff;
  border-radius: 10px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;
}

.prompt-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(22, 93, 255, 0.2);
}


.prompt-card-header {
  padding: 15px 20px;
  background-color: #f8f9fa;
}

.prompt-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  line-height: 1;
}

.prompt-header-meta {
  display: flex;
  align-items: center;
  gap: 20px;
}

.prompt-category {
  background-color: #e9ecef;
  color: #495057;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
}

.prompt-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #165DFF;
  flex: 1;
}


.prompt-card-body {
  padding: 20px;
}

.prompt-content {
  background-color: #f8f9fa;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 15px;
  max-height: 120px;
  overflow-y: auto;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.5;
}

.prompt-input {
  background-color: #f8f9fa;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 15px;
  max-height: 120px;
  overflow-y: auto;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.5;
}

.prompt-content pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.prompt-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 15px;
}

.tag {
  background-color: #e9ecef;
  color: #495057;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
}

.prompt-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px 20px;
}

.prompt-meta {
  display: flex;
  gap: 15px;
  font-size: 15px;
  color: #999;
  flex-wrap: wrap;
}

.prompt-meta i {
  margin-right: 5px;
}


.prompt-actions {
  display: flex;
  gap: 8px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 5px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.btn-small {
  padding: 6px 12px;
  font-size: 13px;
}

.btn-primary {
  background-color: #667eea;
  color: white;
}

.btn-primary:hover {
  background-color: #5568d3;
}

.btn-secondary {
  background-color: #f8f9fa;
  color: #666;
  border: 1px solid #ddd;
}

.btn-secondary:hover {
  background-color: #e9ecef;
}





/* 提示词详情视图 */
.prompt-detail-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
}

.prompt-detail-container {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.prompt-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 24px 0;
  margin-bottom: 20px;
}

.prompt-detail-title-section {
  flex: 1;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.prompt-detail-title {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0;
  flex: 1;
}
.prompt-detail-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s;
}

.prompt-detail-close:hover {
  background-color: #f8f9fa;
  color: #333;
}

.prompt-detail-content {
  padding: 0 24px 20px;
}

.prompt-detail-section {
  margin-bottom: 24px;
}

.prompt-detail-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.prompt-detail-text {
  background-color: #f8f9fa;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 16px;
  font-size: 15px;
  line-height: 1.6;
  color: #333;
  margin: 0;
}

.prompt-detail-code {
  background-color: #f8f9fa;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 16px;
  font-size: 15px;
  line-height: 1.6;
  color: #333;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.5;
}

.prompt-detail-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.prompt-detail-tag-item {
  background-color: #e9ecef;
  color: #495057;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
}

.prompt-detail-footer {
  background-color: #f8f9fa;
  border-top: 1px solid #eee;
  padding: 16px 24px;
}

.prompt-detail-meta {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.prompt-detail-meta-item {
  font-size: 14px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 5px;
}

.prompt-detail-meta-item i {
  margin-right: 5px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .navbar-menu {
    display: none;
  }
  
  .navbar-toggle {
    display: block;
  }
  
  .prompt-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .prompt-detail-container {
    margin: 20px;
    max-height: calc(100vh - 40px);
  }
  
  .prompt-detail-header {
    padding: 20px 20px 0;
  }
  
  .prompt-detail-title {
    font-size: 20px;
  }
  
  .prompt-detail-content {
    padding: 0 20px 16px;
  }
  
  .prompt-detail-text,
  .prompt-detail-code {
    padding: 12px;
  }
  
  .prompt-detail-footer {
    padding: 12px 20px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 28px;
  }
  
  .page-subtitle {
    font-size: 16px;
  }
  
  .prompt-grid {
    grid-template-columns: 1fr;
  }
  
  .prompt-detail-overlay {
    padding: 10px;
  }
  
  .prompt-detail-container {
    margin: 10px;
    max-height: calc(100vh - 20px);
  }
  
  .prompt-detail-title {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .prompt-detail-footer .prompt-meta {
    flex-direction: column;
    gap: 8px;
  }
}
</style>